import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import type { RootState } from "../store"; // <-- adjust path if needed

/** Contract shape returned by the API */
export type DashboardContract = {
  id: string;
  start_date?: string;
  end_date?: string;
  category_id?: string;
  category_name?: string; // optional string
  rate_per_gram?: string;
  assigned_weight?: string;
  used_weight?: string;
  remaining_weight?: string;
  assigned_value?: string;
  used_value?: string;
  remaining_value?: string;
  billing_cycle?: string;
  active?: string;
  view_link?: string;
};

export type CustomerDashboardData = {
  pickedup_count: number;
  out_for_delivery_count: number;
  waiting_for_pickup_count: number;
  shipped_count: number;
  approved_count: number;
  archived_count: number;
  active_contract_id: number;

  contracts?: DashboardContract[];

  assigned_value_sum?: string;
  used_value_sum?: string;
  remaining_value_sum?: string;

  category_name?: string;
};

type State = {
  loading: boolean;
  error: string | null;
  data: CustomerDashboardData | null;

  assignedValue: string;
  usedValue: string;
  remainingValue: string;
  categoryName: string;

  // ✅ per-customer dedupe/TTL
  loadingById: Record<string, boolean>;
  loadedById: Record<string, boolean>;
  lastFetchedAtById: Record<string, number>;
};

const initialState: State = {
  loading: false,
  error: null,
  data: null,
  assignedValue: "0.00",
  usedValue: "0.00",
  remainingValue: "0.00",
  categoryName: "",

  loadingById: {},
  loadedById: {},
  lastFetchedAtById: {},
};

const apiClient = axios.create({
  baseURL: "https://my.axlpl.com/messenger/services_v7",
  headers: { Accept: "application/json" },
});

type FetchArg = string | { customer_id: string; force?: boolean }; // allow force refresh

const getIdFromArg = (arg: FetchArg): { id: string; force: boolean } => {
  if (typeof arg === "string") return { id: arg, force: false };
  return { id: arg.customer_id, force: !!arg.force };
};

export const fetchCustomerDashboard = createAsyncThunk<
  CustomerDashboardData,
  FetchArg,
  { state: RootState; rejectValue: string }
>(
  "customerDashboard/fetch",
  async (arg, { rejectWithValue }) => {
    try {
      const { id } = getIdFromArg(arg);
      const fd = new FormData();
      fd.append("customer_id", id);

      const res = await apiClient.post("/customerdashboard", fd);
      const d = res.data;

      if (d?.status !== "success") {
        return rejectWithValue(d?.message || "Failed to fetch dashboard");
      }

      const toNum = (v: any) => {
        if (v == null) return 0;
        const parsed = Number(String(v).trim());
        return Number.isFinite(parsed) ? parsed : 0;
      };

      const contracts: DashboardContract[] = Array.isArray(d.contracts)
        ? d.contracts
        : [];

      const payload: CustomerDashboardData = {
        pickedup_count: toNum(d.pickedup_count),
        out_for_delivery_count: toNum(d.out_for_delivery_count),
        waiting_for_pickup_count: toNum(d.waiting_for_pickup_count),
        shipped_count: toNum(d.shipped_count),
        approved_count: toNum(d.approved_count),
        archived_count: toNum(d.archived_count),
        active_contract_id: toNum(d.active_contract_id),
        contracts,
        assigned_value_sum: d.assigned_value_sum ?? undefined,
        used_value_sum: d.used_value_sum ?? undefined,
        remaining_value_sum: d.remaining_value_sum ?? undefined,
        category_name: d.category_name ?? undefined,
      };

      return payload;
    } catch (e: any) {
      return rejectWithValue(
        e?.response?.data?.message || e.message || "Error",
      );
    }
  },
  {
    // ✅ Prevent duplicate calls for same customer_id
    condition: (arg, { getState }) => {
      const { id, force } = getIdFromArg(arg);
      const s = (getState() as RootState).customerDashboard;

      if (!id) return false;
      if (force) return true;

      // Skip if already loading for this id
      if (s.loadingById[id]) return false;

      // Skip if already loaded for this id
      if (s.loadedById[id]) {
        // Optional TTL: refetch after N ms
        // const ttlMs = 120_000; // 2 minutes
        // const last = s.lastFetchedAtById[id] || 0;
        // if (Date.now() - last > ttlMs) return true; // refetch after TTL
        return false;
      }

      return true;
    },
  },
);

const customerDashboardSlice = createSlice({
  name: "customerDashboard",
  initialState,
  reducers: {
    resetCustomerDashboard(state) {
      state.loading = false;
      state.error = null;
      state.data = null;
      state.assignedValue = "0.00";
      state.usedValue = "0.00";
      state.remainingValue = "0.00";
      state.categoryName = "";
      state.loadingById = {};
      state.loadedById = {};
      state.lastFetchedAtById = {};
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCustomerDashboard.pending, (s, a) => {
      const { id } = getIdFromArg(a.meta.arg);
      s.loading = true; // keep if you use a global spinner
      s.error = null;
      s.loadingById[id] = true;
      // NOTE: don't clear s.data here to avoid UI flicker during refresh
    });

    builder.addCase(fetchCustomerDashboard.fulfilled, (s, action) => {
      s.loading = false;
      s.data = action.payload ?? null;

      const fromPayloadAssigned = action.payload?.assigned_value_sum;
      const fromPayloadUsed = action.payload?.used_value_sum;
      const fromPayloadRemaining = action.payload?.remaining_value_sum;

      const contracts = action.payload?.contracts ?? [];

      if (fromPayloadAssigned || fromPayloadUsed || fromPayloadRemaining) {
        s.assignedValue = fromPayloadAssigned ?? "0.00";
        s.usedValue = fromPayloadUsed ?? "0.00";
        s.remainingValue = fromPayloadRemaining ?? "0.00";
      } else {
        const sum = (key: keyof DashboardContract) =>
          contracts.reduce((acc, c) => {
            const v = c[key];
            const n = v == null ? 0 : Number(String(v).trim());
            return acc + (Number.isFinite(n) ? n : 0);
          }, 0);

        s.assignedValue = sum("assigned_value").toFixed(2);
        s.usedValue = sum("used_value").toFixed(2);
        s.remainingValue = sum("remaining_value").toFixed(2);
      }

      const firstContractCategory =
        contracts.length > 0 && contracts[0].category_name
          ? contracts[0].category_name
          : "";
      const topLevelCategory =
        action.payload && action.payload.category_name
          ? action.payload.category_name
          : "";
      s.categoryName = firstContractCategory || topLevelCategory || "";

      // ✅ mark this id as loaded
      const id =
        (action.meta.arg as any)?.customer_id ||
        (typeof action.meta.arg === "string" ? action.meta.arg : "");
      if (id) {
        s.loadingById[id] = false;
        s.loadedById[id] = true;
        s.lastFetchedAtById[id] = Date.now();
      }
    });

    builder.addCase(fetchCustomerDashboard.rejected, (s, a) => {
      s.loading = false;
      s.error = a.payload ?? "Failed to fetch dashboard";
      const { id } = getIdFromArg(a.meta.arg);
      s.loadingById[id] = false;
      // don't mark loaded on failure
    });
  },
});

export const { resetCustomerDashboard } = customerDashboardSlice.actions;
export default customerDashboardSlice.reducer;
